import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Indicator,
  Indicator2Values,
  Model,
  Result,
  ScoreCard,
} from '@arra/core';
import styled from '@emotion/styled';
import { LinearProgress } from '@mui/material';
import BaseBlock from '../ui/Block';
import Button from '../ui/Button';

import Indicator2ValuesComponent from './Indicator2ValuesComponent';
import IndicatorComponent from './IndicatorComponent';
import ScoreCardComponent from './ScoreCardComponent';

import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { getServiceType } from '../analytics/posthogEvents';
import Co2Icon from '../ui/icons/categories/co2.png';
import CoalIcon from '../ui/icons/categories/ressources.png';
import EnergyIcon from '../ui/icons/categories/energy-water.png';
import WaterIcon from '../ui/icons/categories/energy-water.png';
import VolunteerActivismIcon from '../ui/icons/categories/social-utility.png';

const Block = styled(BaseBlock)`
  background-color: var(--results-bg);
  gap: 16px;
  height: auto;
  justify-content: flex-start;
`;

interface ResultsSimpleProps {
  results?: Result[];
  model: Model;
  loading: boolean;
}

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ResultsSimple = ({ results, model, loading }: ResultsSimpleProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['project']);
  const posthog = usePostHog();
  return (
    <Block>
      <p className="h4b">{t('project:results-block.title')}</p>
      <Button
        color="primary"
        variant="contained"
        onClick={() => {
          posthog?.capture('results viewed', {
            step_name: 'results_viewed',
            step_order: 2,
            service_type: getServiceType(model.type),
            from_page: 'project',
            to_page: 'results',
          });
          navigate(`./results`, { state: { from_page: 'project' } });
        }}
        fullWidth
      >
        {t('project:results-block.full-results')}
      </Button>
      <ResultsContainer>
        {(results || [])
          .filter((result) => result.scope === 'simple')
          .map((result, index) => {
            return (
              <ResultDispatch
                result={result}
                loading={loading}
                key={index}
                model={model}
              />
            );
          })}
      </ResultsContainer>
    </Block>
  );
};

interface ResultDispatchProps {
  result: Result;
  loading: boolean;
  model: Model;
}

//IT IS NEEDED TO DISPATCH ASAP AS TYPE OF RESULTS DOESN'T HAVE THE SAME PROPERTIES
const ResultDispatch = ({ result, loading, model }: ResultDispatchProps) => {
  switch (result.type) {
    case 'indicator':
      return (
        <ResultSection result={result} loading={loading}>
          <IndicatorComponent result={result} modelName={model.name} />
        </ResultSection>
      );
    case 'indicator2Values':
      return (
        <ResultSection result={result} loading={loading}>
          <Indicator2ValuesComponent result={result} model={model} />
        </ResultSection>
      );
    case 'scoreCard':
      return (
        <ResultSection result={result} loading={loading}>
          <ScoreCardComponent result={result} model={model} />
        </ResultSection>
      );
  }
  return null;
};

const ResultSectionContainer = styled.div`
  background-color: white;
  width: 100%;
  position: relative;
  padding: 9px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  overflow: hidden;
  @media screen and (max-height: 800px) {
    padding: 8px;
  }
  @media screen and (max-height: 600px) {
    padding: 6px;
  }
`;

interface ResultSectionProps {
  result: Indicator | ScoreCard | Indicator2Values;
  children: JSX.Element | JSX.Element[];
  loading: boolean;
}

export const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 1;
  @media screen and (max-height: 700px) {
    width: 30px;
    height: 30px;
  }
  @media screen and (max-height: 600px) {
    width: 20px;
    height: 20px;
  }
`;

export const scopeIcons: { [key: string]: ReactElement } = {
  co2: <img src={Co2Icon} alt="co2" style={{ width: '32px'}} />,
  social_utility: <img src={VolunteerActivismIcon} alt="social_utility" style={{ width: '32px'}} />,
  ressources: <img src={CoalIcon} alt="ressources" style={{ width: '32px'}} />,
  energie: <img src={EnergyIcon} alt="energie" style={{ width: '32px'}} />,
  eau: <img src={WaterIcon} alt="eau" style={{ width: '32px'}} />,
  grilles: <CalendarViewMonthIcon />,
};

export const ResultSection = ({
  result,
  loading,
  children,
}: ResultSectionProps) => {
  return (
    <ResultSectionContainer key={result.title}>
      <LoadingIndicator loading={loading} />
      {children}
    </ResultSectionContainer>
  );
};

export const TitleScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex-shrink: 1;
  p {
    font-size: 15px;
  }
`;

const LoadingIndicatorContainer = styled.div`
  height: 4px;
  opacity: 0;
  transition: opacity 250ms;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  &.loading {
    opacity: 0.4;
  }
`;

const LoadingIndicator = ({ loading }: { loading: boolean }) => (
  <LoadingIndicatorContainer className={loading ? 'loading' : ''}>
    <LinearProgress />
  </LoadingIndicatorContainer>
);

export default ResultsSimple;
